'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Building, User, Phone, Mail, Globe, MapPin } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { customers } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import type { Customer } from '../types';

// 고객 등록 모달
function CustomerAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="고객 등록"
      description="새로운 고객 정보를 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="고객코드" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="고객유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="company">기업</option>
              <option value="individual">개인</option>
            </FormSelect>
          </FormField>
          <FormField label="고객명" required>
            <FormInput placeholder="고객명을 입력하세요" />
          </FormField>
          <FormField label="업종">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="식품">식품</option>
              <option value="제조">제조</option>
              <option value="유통">유통</option>
              <option value="물류">물류</option>
              <option value="전자상거래">전자상거래</option>
              <option value="기타">기타</option>
            </FormSelect>
          </FormField>
          <FormField label="이메일">
            <FormInput type="email" placeholder="example@email.com" />
          </FormField>
          <FormField label="전화번호">
            <FormInput placeholder="02-000-0000" />
          </FormField>
          <FormField label="담당자">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="u001">김영업</option>
              <option value="u002">이마케팅</option>
              <option value="u003">박관리</option>
            </FormSelect>
          </FormField>
          <FormField label="웹사이트">
            <FormInput placeholder="https://" />
          </FormField>
        </div>
        <FormField label="주소">
          <FormTextarea rows={2} placeholder="주소를 입력하세요" />
        </FormField>
        <FormField label="비고">
          <FormTextarea rows={2} placeholder="추가 정보를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function CustomerListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return customers.filter((customer) => {
      const matchesSearch =
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (customer.email && customer.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        customer.code.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = selectedType === 'all' || customer.type === selectedType;
      const matchesActive = showInactive || customer.isActive;

      return matchesSearch && matchesType && matchesActive;
    });
  }, [searchQuery, selectedType, showInactive]);

  // 통계
  const stats = useMemo(() => {
    const active = customers.filter((c) => c.isActive).length;
    const totalRevenue = customers.reduce((sum, c) => sum + c.totalRevenue, 0);
    const companies = customers.filter((c) => c.type === 'company').length;
    const individuals = customers.filter((c) => c.type === 'individual').length;

    return { active, totalRevenue, companies, individuals };
  }, []);

  const columns = [
    {
      key: 'code',
      label: '고객코드',
      width: '10%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary whitespace-nowrap">{String(value)}</span>
      ),
    },
    {
      key: 'name',
      label: '고객명',
      width: '22%',
      sortable: true,
      render: (value: unknown, row: Customer) => (
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            row.type === 'company'
              ? 'bg-blue-100 dark:bg-blue-900/30'
              : 'bg-green-100 dark:bg-green-900/30'
          }`}>
            {row.type === 'company' ? (
              <Building className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            ) : (
              <User className="w-4 h-4 text-green-600 dark:text-green-400" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{String(value)}</div>
            {row.industry && (
              <div className="text-xs text-muted-foreground truncate">{row.industry}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      label: '연락처',
      width: '22%',
      render: (value: unknown, row: Customer) => (
        <div className="text-sm min-w-0">
          {value ? (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Mail className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{String(value)}</span>
            </div>
          ) : null}
          {row.phone && (
            <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
              <Phone className="w-3 h-3 flex-shrink-0" />
              <span className="whitespace-nowrap">{row.phone}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'totalRevenue',
      label: '총 거래액',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{value ? formatCurrency(value as number) : '-'}</span>
      ),
    },
    {
      key: 'assignedToName',
      label: '담당자',
      width: '10%',
      render: (value: unknown) => (
        <span className="text-sm whitespace-nowrap">{String(value || '-')}</span>
      ),
    },
    {
      key: 'isActive',
      label: '상태',
      width: '8%',
      render: (value: unknown) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            value
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
          }`}
        >
          {value ? '활성' : '비활성'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="고객 관리"
        description="고객 정보를 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            고객 등록
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">활성 고객</div>
          <div className="text-2xl font-bold mt-1">{stats.active}개사</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 거래액</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(stats.totalRevenue)}</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">기업 고객</div>
          <div className="text-2xl font-bold mt-1">{stats.companies}개사</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">개인 고객</div>
          <div className="text-2xl font-bold mt-1">{stats.individuals}명</div>
        </div>
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="고객코드, 이름, 이메일 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 유형</option>
          <option value="company">기업</option>
          <option value="individual">개인</option>
        </select>

        <button
          onClick={() => setShowInactive(!showInactive)}
          className={`h-10 px-4 rounded-lg border text-sm transition-colors ${
            showInactive
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-background text-muted-foreground'
          }`}
        >
          비활성 포함
        </button>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 고객 등록 모달 */}
      <CustomerAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
